import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { qduelsApi, useGetRatingQuery } from '@/services/qduelsApi';
import { listVerdict } from '@/lib/listState';
import { useStalled, useEverFailed, NO_ANSWER_MS } from '@/hooks/useStalled';
import { PanelNotice, PanelWaiting } from '@/components/hub/PanelNotice';
import { useWallets } from '@/hooks/useWallets';
import { monetaDelLivelloOf } from '@/lib/wallet/livello';
import { env } from '@/env';
import { useAppDispatch } from '@/store/hooks';
import { useHaptics } from '@/hooks/useHaptics';
import { QueueSheet } from '@/components/social/QueueSheet';
import { RankedCircle } from '@/components/duels/RankedCircle';
import type { QduelsRating } from '@/types/qduels';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import { formatNumero } from '@/lib/fmt';

/**
 * Riga sotto il titolo: modalità e rank insieme ("1v1 - Gold").
 *
 * Stanno su una riga sola perché rispondono alla stessa domanda — "in che cosa
 * sono, e a che livello". Separarli costringerebbe a leggere due volte.
 */
function RankedModeAndTier({ rating }: { rating: QduelsRating | undefined }) {
  const { t } = useTranslation('duels');
  const tier = rating?.tier?.name ?? t('ranked.unranked', { defaultValue: 'Non classificato' });
  return <span className="truncate text-sm font-semibold text-fg-muted">1v1 - {tier}</span>;
}

/**
 * 1v1 Ranked — the ranked circle: tier progress and the placement checklist
 * around a central Play button.
 *
 * The rating fields were already coming down from qduels and the client threw
 * them away; nothing here needed a backend change.
 */
export function RankedPanel() {
  const { t } = useTranslation('hub');
  const lingua = useLinguaFormato();
  const dispatch = useAppDispatch();
  const haptics = useHaptics();
  const [queueOpen, setQueueOpen] = useState(false);

  // ⚠️ `skip` quando qduels e' spento su questo ambiente: la chiamata non partirebbe
  // comunque (v. `services/qduelsApi.ts`), ma senza `skip` la query resterebbe in
  // `isLoading` per un istante e il pannello mostrerebbe il cerchio in caricamento
  // prima di dire la verita'. Il flag non e' un ripiego dell'errore: e' un fatto noto
  // PRIMA di chiedere.
  const qduelsOn = env.featureQduelsEnabled;
  const { data: rating, isLoading, isError } = useGetRatingQuery(undefined, { skip: !qduelsOn });

  // 🔴 QUESTO PANNELLO NON AVEVA ALCUN RAMO D'ERRORE, ed era il piu' costoso degli otto.
  // Misurato su `stg-tma` il 2026-09-06 (or45): senza risposta da qduels lo schermo
  // affermava «1v1 - Non classificato», «Lv. —» e teneva VIVO il bottone TROVA PARTITA —
  // che non e' una lettura: entra in una coda ranked e lascia un ELO scritto. Un invito a
  // toccare qualcosa che il backend non puo' servire e' peggio di un errore.
  //
  // ⚠️ «Non classificato» resta la verita' per chi e' davvero senza rank: quel caso e' un
  // **200 con `tier: null`**, non un errore. La distinzione la fa la RISPOSTA, non l'assenza
  // di dati — che e' precisamente cio' che qui mancava.
  const answered = rating !== undefined;
  const everFailed = useEverFailed(isError, answered);
  const pending = !answered && !everFailed;
  const stalled = useStalled(pending);
  const noAnswer = useStalled(pending, NO_ANSWER_MS);
  const verdict = listVerdict(rating ? 1 : 0, [
    {
      // 🔴 «Spenta da configurazione» NON e' «non ha risposto», ed e' l'unica fonte di
      // questo pannello: con qduels spento il verdetto e' `unavailable`, non `error`.
      // [GO di Luca 07/09 21:16:16Z, L-565.] Precedenze in `lib/listState.ts`.
      reachable: qduelsOn,
      loading: isLoading,
      slow: stalled,
      failed: everFailed || noAnswer,
      answered,
    },
  ]);

  // The wallet LEVEL for the RATING coin. The qduels XP *is* the QRAT balance in
  // QBank; the level is computed by QBank on that wallet. One coin, two reads —
  // the rating query owns the number, the wallet query owns the level.
  //
  // Matched by CODE, not by "the first wallet that happens to have a level": QRAT first,
  // in ANY position; otherwise the one non-Elo coin with a level; otherwise none — we never
  // guess between two. The rule lives ONCE, in `lib/wallet/livello.ts`
  // (`monetaDelLivelloOf`), shared with `LevelBadge` and `PlayerIdentity`. Elo coins
  // (`ELO-<gioco>-<modalita'>`, ADR-0008 §2) are `model=1` like the EXP but are NOT the
  // player's level, and never candidates.
  const walletsQ = useWallets({ skip: !env.featureQbankEnabled });
  const ratingWallet = monetaDelLivelloOf(walletsQ.data);
  const expLevel = ratingWallet?.level ?? null;

  /**
   * The payoff lands on the way OUT.
   *
   * A match settles inside the sheet, so the rating behind it is already stale
   * by the time the player closes it. Invalidating on close refetches once and
   * lets the new tick light up as the circle comes back — after the game, which
   * is when it means something. Polling while the sheet is open would spend
   * requests updating something nobody can see.
   */
  const closeQueue = () => {
    setQueueOpen(false);
    dispatch(qduelsApi.util.invalidateTags(['Rating']));
    haptics.impact('light');
  };

  if (verdict === 'loading' || verdict === 'slow') {
    // Il cerchio in stato di caricamento TACE: nessuna intestazione, perche' l'intestazione
    // e' fatta di affermazioni («Non classificato», «Lv. —») che non abbiamo ancora il
    // diritto di fare. Oltre la soglia di pazienza il cerchio resta e sotto compare la riga
    // che dice che ci sta mettendo troppo — non che e' andata male.
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 pt-3">
        <RankedCircle rating={undefined} loading onPlay={() => {}} />
        {/* Lo spinner qui e' gia' il cerchio: `spinner={false}` evita di disegnarne due. */}
        {verdict === 'slow' && <PanelWaiting slow spinner={false} className="h-auto" />}
      </div>
    );
  }
  if (verdict !== 'rows') {
    // Niente `<RankedCircle>` e niente `<QueueSheet>`: senza il foglio non esiste il
    // bottone che entra in coda, ed e' voluto — non si offre un'azione che scrive verso un
    // servizio che non ha risposto.
    // ⛔ I due toni NON si collassano: `unavailable` dice «riprovare non serve, questo
    // backend non c'e' qui», `error` dice «non ha risposto, riprovare puo' servire». La
    // differenza non e' cosmetica, e' cio' che l'utente puo' farci — e prima di oggi
    // questo ramo diceva «error» anche quando la fonte era spenta per costruzione.
    // Lo slot della strada alternativa vale per `unavailable`, che e' lo stato «questa
    // fonte non c'e' piu' qui»; su `error` la fonte esiste e non ha risposto, e mandare
    // altrove sarebbe un consiglio dato su un fatto diverso. Estenderlo e' una decisione
    // a se', non un'ovvieta'.
    return verdict === 'unavailable' ? (
      <PanelNotice
        tone="unavailable"
        via={t('rankedUnavailableVia', {
          defaultValue: 'In the meantime you can find tournaments in {{section}}.',
          // Il nome della scheda viene dalla scheda, non da una copia: se domani «Eventi»
          // cambia etichetta, la frase la segue invece di mandare l'utente a cercare una
          // voce di menu che non esiste piu'.
          // ⚠️ Con un `defaultValue`: senza, una chiave mancante lascerebbe un BUCO in mezzo
          // alla frase («…trovi i tornei in .»), e un buco in una strada e' peggio di una
          // strada scritta a mano.
          section: t('sections.events', { defaultValue: 'Events' }),
        })}
      />
    ) : (
      <PanelNotice tone="error" />
    );
  }

  return (
    <div className="flex flex-col gap-4 pt-3">
      {/* Intestazione su due colonne: a sinistra CHI SEI (sezione + modalità e
          rank), a destra QUANTO VALI (livello + EXP). Prima era una pila
          centrata sopra il cerchio; separare identità e progresso rende
          leggibili entrambi in un colpo d'occhio invece di impilarli. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. Il modo e la fascia qui sotto sono DATI e restano. */}
          <span className="sr-only">
            {t('sections.ranked', { defaultValue: 'Ranked' })}
          </span>
          <RankedModeAndTier rating={rating} />
        </div>

        <div className="flex shrink-0 flex-col items-end">
          <span className="text-2xl font-extrabold leading-tight text-fg">
            {expLevel != null ? t('ranked.levelShort', { level: expLevel }) : '—'}
          </span>
          <span className="text-sm font-semibold tabular-nums text-fg-muted">
            {rating?.xp != null ? t('ranked.exp', { xp: formatNumero(rating.xp, lingua) }) : ''}
          </span>
        </div>
      </div>

      {/* Il cerchio resta centrato per conto suo: l'intestazione sopra occupa
          tutta la larghezza, quindi il contenitore non può più centrare tutto. */}
      <div className="flex justify-center">
        <RankedCircle rating={rating} loading={isLoading} onPlay={() => setQueueOpen(true)} />
      </div>

      {/* Niente `queueOpen &&` davanti: smontando alla chiusura il pannello
          sparirebbe prima di poter scivolare via. La visibilità la governa il
          foglio, che resta montato per la durata dell'uscita. */}
      <QueueSheet open={queueOpen} onClose={closeQueue} />
    </div>
  );
}
